import { flatten } from 'lodash';

import { ForeignProductIndividualItemPurpose } from 'api';
import { DrivelineSubSystem } from 'api/responses/models/Unit/UnitStructure/DrivelineSubSystem';
import { InstallationSubSystem } from 'api/responses/models/Unit/UnitStructure/InstallationSubSystem';
import { ProductIndividualItemPurpose } from 'api/responses/models/Unit/UnitStructure/ProductIndividualItemPurpose';
import {
	DrivelineDto,
	ProductIndividualDto,
} from 'api/responses/models/UnitCreation/ProductIndividualSearchResultDto';
import { isMainType } from 'domain/productIndividual/ProductIndividual';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { generateUUID } from 'library/utils/generateUuid';

import {
	ForeignProductFormData,
	ForeignProductIndividualDto,
	Installation,
} from './Types';

const DEFAULT_SORT_POSITION = 99;

export const mapProductIndividualItemPurposeToProductIndividualDto = ({
	id,
	description,
	designation,
	highestOpenCampaignType,
	latestKnownOperationHoursDate,
	partNumber,
	productName,
	productCategory,
	serialNumber,
	status,
	sortPosition,
	energyThroughput,
	operatingHours,
}: ProductIndividualItemPurpose): ProductIndividualDto => ({
	id,
	configurationId: null,
	campaignType: highestOpenCampaignType ?? undefined,
	description,
	designation,
	generatedUuid: generateUUID(),
	latestKnownOperationHoursDateFormatted: getLocalizedDateFormat(
		latestKnownOperationHoursDate
	),
	isProductWithoutDriveline: !isMainType(productCategory),
	isValidStatus: true,
	partNumber,
	productName,
	serialNumber: serialNumber || '',
	sortPosition,
	status: status ?? 'Null',
	type: productCategory,
	energyThroughput: energyThroughput ?? undefined,
	operatingHours: operatingHours ?? undefined,
});

export const mapDrivelineSubSystemToDrivelineDto = ({
	chassisNumber,
	chassisSeries,
	id,
	position,
	productIndividualSubSystems,
}: DrivelineSubSystem): DrivelineDto => ({
	chassisId: `${chassisSeries} ${chassisNumber}`,
	chassisSeries,
	chassisNumber,
	drivelinePosition: position,
	chassisPosition: null,
	chassisType: null,
	id,
	configurationId: null,
	productIndividuals: flatten(
		productIndividualSubSystems.map(({ productIndividuals }) =>
			productIndividuals.map((product) =>
				mapProductIndividualItemPurposeToProductIndividualDto(product)
			)
		)
	),
	foreignProductIndividuals: flatten(
		productIndividualSubSystems.map(({ foreignProductIndividuals }) =>
			foreignProductIndividuals.map(
				mapForeignProductIndividualItemPurposeToDto
			)
		)
	),
});

export const mapForeignProductIndividualItemPurposeToDto = ({
	id,
	productCategory,
	serialNumber,
	description,
	partNumber,
	commentsCount,
	sortPosition,
}: ForeignProductIndividualItemPurpose): ForeignProductIndividualDto => ({
	id,
	type: productCategory,
	serialNumber,
	description: description ?? undefined,
	partNumber: partNumber ?? undefined,
	commentsCount: commentsCount ?? 0,
	sortPosition,
	generatedUuid: generateUUID(),
});

export const mapInstallationSubSystemToInstallation = (
	{
		id,
		detailedSegment,
		drivelines,
		purpose,
		operatingProfileValue,
		sortPosition,
	}: InstallationSubSystem,
	generatedUuid?: string
): Installation => ({
	id,
	configurationId: null,
	detailedSegmentKey: detailedSegment,
	generatedUuid: generatedUuid || generateUUID(),
	installationPurposeKey: purpose || null,
	drivelines: drivelines.map(mapDrivelineSubSystemToDrivelineDto),
	operatingProfileKey: operatingProfileValue,
	sortPosition,
});

export const mapForeignProductFormDataToProductIndividual = (
	data: ForeignProductFormData,
	sortPosition: number | undefined
): ForeignProductIndividualDto => ({
	...data,
	generatedUuid: data.generatedUuid || generateUUID(),
	sortPosition: sortPosition ?? DEFAULT_SORT_POSITION,
	commentsCount: 0,
	id: 0,
});

export const mapProductIndividualToForeignProductFormData = ({
	serialNumber,
	description,
	type,
	partNumber,
	generatedUuid,
}: ForeignProductIndividualDto): ForeignProductFormData => ({
	serialNumber,
	description,
	type,
	partNumber,
	generatedUuid,
});
